'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { stringifyUrl } from 'query-string';
import { forwardRef, useMemo, useRef, useState } from 'react';
import { highResMedia, lgEndMedia, lgEndPixels, lgMedia, lgStartMedia, lgStartPixels, mdEndMedia, mdEndPixels, mdMedia, pageLayoutMaxWidthPixels, smEndMedia, smMedia, xlStartMedia, xsEndMedia, xsEndPixels, } from '../../constants';
import * as Styles from './styles';
export const popularIconWidth = 42;
const popularPhoneWidth = 440;
const popularDesktopWidth = 1080;
const selectedDesktopWidth = Math.max(popularDesktopWidth, lgStartPixels);
const employeeDesktopWidth = 1800;
const responsiveImageWidths = [
    popularIconWidth,
    popularPhoneWidth / 2,
    popularPhoneWidth,
    popularPhoneWidth * 2,
    selectedDesktopWidth,
    selectedDesktopWidth * 2,
    employeeDesktopWidth * 2,
];
const gifCheckingRegex = new RegExp(/\.gif($|\?)/);
export const getShopifyDynamicSrc = ({ src, width }) => {
    return stringifyUrl({
        url: src,
        query: {
            width,
        },
    });
};
export const getContentfulDynamicSrc = ({ src, width }) => {
    if (gifCheckingRegex.test(src))
        return src;
    return stringifyUrl({
        url: src,
        query: {
            w: width,
            fm: 'webp',
        },
    });
};
const createURLFromSource = (src) => {
    try {
        if (src.startsWith('//'))
            return new URL(`https:${src}`);
        return new URL(src);
    }
    catch {
        return null;
    }
};
const getResponsiveSrcInfo = ({ src, width, }) => {
    const srcAsUrl = createURLFromSource(src);
    const maxWidth = responsiveImageWidths[responsiveImageWidths.length - 1];
    const computedWidth = width
        ? responsiveImageWidths.find(responsiveImageWidth => {
            return responsiveImageWidth >= width || responsiveImageWidth === maxWidth;
        })
        : undefined;
    if (srcAsUrl === null) {
        return {
            origin: 'none',
            src: null,
        };
    }
    else if (srcAsUrl.host === 'cdn.shopify.com') {
        const dynamicSrc = getShopifyDynamicSrc({
            src: srcAsUrl.protocol + '//www.wearfigs.com/i/shopify' + srcAsUrl.pathname + srcAsUrl.search,
            width: computedWidth,
        });
        return {
            origin: 'shopify',
            src: dynamicSrc,
        };
    }
    else if (srcAsUrl.host === 'images.ctfassets.net') {
        const dynamicSrc = getContentfulDynamicSrc({
            src: srcAsUrl.protocol + '//www.wearfigs.com/i/contentful' + srcAsUrl.pathname + srcAsUrl.search,
            width: computedWidth,
        });
        return {
            origin: 'contentful',
            src: dynamicSrc,
        };
    }
    else {
        return {
            origin: 'other',
            src: srcAsUrl.toString(),
        };
    }
};
const PictureOrFragment = ({ children, asPicture, }) => {
    if (asPicture)
        return _jsx("picture", { children: children });
    return _jsx(_Fragment, { children: children });
};
export const ResponsiveImage = forwardRef(({ alt, aspectRatios, src, loading, widths, srcs, transparentBackground = false, ...props }, ref) => {
    const computedLoading = loading === 'lazy' ? 'lazy' : undefined;
    const computedXsWidth = widths.xs ?? widths.sm;
    const computedSmWidth = widths.sm;
    const computedMdWidth = widths.md;
    const computedLgWidth = widths.lg ?? widths.md;
    const computedXlWidth = widths.xl ?? widths.lg ?? widths.md;
    const xsWidthPixels = widths.unit === 'px' ? computedXsWidth : (computedXsWidth / 100) * xsEndPixels;
    const smWidthPixels = widths.unit === 'px' ? computedSmWidth : (computedSmWidth / 100) * popularPhoneWidth;
    const mdWidthPixels = widths.unit === 'px' ? computedMdWidth : (computedMdWidth / 100) * mdEndPixels;
    const lgWidthPixels = widths.unit === 'px' ? computedLgWidth : (computedLgWidth / 100) * lgEndPixels;
    const xlWidthPixels = widths.unit === 'px' ? computedXlWidth : (computedXlWidth / 100) * pageLayoutMaxWidthPixels;
    const pictureTagSources = useMemo(() => {
        if (!srcs)
            return [];
        const pictureTagSources = [];
        const computedXsSrc = srcs.xs ?? srcs.sm;
        const computedSmSrc = srcs.sm;
        const computedMdSrc = srcs.md;
        const computedLgSrc = srcs.lg ?? srcs.md;
        const computedXlSrc = srcs.xl ?? srcs.lg ?? srcs.md;
        pictureTagSources.push({
            ...getResponsiveSrcInfo({
                src: computedXsSrc,
                width: xsWidthPixels,
            }),
            media: xsEndMedia,
        });
        pictureTagSources.push({
            ...getResponsiveSrcInfo({
                src: computedSmSrc,
                width: smWidthPixels * 2,
            }),
            media: `${smMedia} and ${highResMedia}`,
        });
        pictureTagSources.push({
            ...getResponsiveSrcInfo({
                src: computedSmSrc,
                width: smWidthPixels,
            }),
            media: smMedia,
        });
        pictureTagSources.push({
            ...getResponsiveSrcInfo({
                src: computedMdSrc,
                width: mdWidthPixels,
            }),
            media: mdMedia,
        });
        pictureTagSources.push({
            ...getResponsiveSrcInfo({
                src: computedLgSrc,
                width: lgWidthPixels * 2,
            }),
            media: `${lgStartMedia} and ${highResMedia}`,
        });
        pictureTagSources.push({
            ...getResponsiveSrcInfo({
                src: computedLgSrc,
                width: lgWidthPixels,
            }),
            media: lgMedia,
        });
        pictureTagSources.push({
            ...getResponsiveSrcInfo({
                src: computedXlSrc,
                width: xlWidthPixels,
            }),
            media: xlStartMedia,
        });
        return pictureTagSources;
    }, [srcs, xsWidthPixels, smWidthPixels, mdWidthPixels, lgWidthPixels, xlWidthPixels]);
    const responsiveSrcInfo = useMemo(() => {
        return getResponsiveSrcInfo({
            src,
        });
    }, [src]);
    const imgTagSizes = useMemo(() => {
        const xlSize = `${computedXlWidth}${widths.unit}`;
        const lgSize = `${lgEndMedia} ${computedLgWidth}${widths.unit}, `;
        const mdSize = `${mdEndMedia} ${computedMdWidth}${widths.unit}, `;
        const smSize = `${smEndMedia} ${computedSmWidth}${widths.unit}, `;
        const xsSize = `${xsEndMedia} ${computedXsWidth}${widths.unit}, `;
        if (responsiveSrcInfo.origin === 'shopify' || responsiveSrcInfo.origin === 'contentful') {
            return `${xsSize ?? ''}${smSize ?? ''}${mdSize ?? ''}${lgSize ?? ''}${xlSize ?? ''}`;
        }
        else {
            return undefined;
        }
    }, [
        responsiveSrcInfo,
        widths,
        computedXsWidth,
        computedSmWidth,
        computedMdWidth,
        computedLgWidth,
        computedXlWidth,
    ]);
    const computedSrc = useMemo(() => {
        if (pictureTagSources[0]?.src)
            return pictureTagSources[0].src;
        if (responsiveSrcInfo.origin === 'shopify') {
            return getShopifyDynamicSrc({
                src: responsiveSrcInfo.src,
                width: responsiveImageWidths[0],
            });
        }
        else if (responsiveSrcInfo.origin === 'contentful') {
            return getContentfulDynamicSrc({
                src: responsiveSrcInfo.src,
                width: responsiveImageWidths[0],
            });
        }
        else {
            return responsiveSrcInfo.src;
        }
    }, [pictureTagSources, responsiveSrcInfo.origin, responsiveSrcInfo.src]);
    const imgTagSrcset = useMemo(() => {
        if (responsiveSrcInfo.origin === 'shopify') {
            return responsiveImageWidths
                .map(responsiveImageWidth => {
                return (getShopifyDynamicSrc({
                    src: responsiveSrcInfo.src,
                    width: responsiveImageWidth,
                }) + ` ${responsiveImageWidth}w`);
            })
                .join(', ');
        }
        else if (responsiveSrcInfo.origin === 'contentful') {
            return responsiveImageWidths
                .map(responsiveImageWidth => {
                return (getContentfulDynamicSrc({
                    src: responsiveSrcInfo.src,
                    width: responsiveImageWidth,
                }) + ` ${responsiveImageWidth}w`);
            })
                .join(', ');
        }
        else {
            return undefined;
        }
    }, [responsiveSrcInfo]);
    if (computedSrc === null)
        return null;
    return (_jsxs(PictureOrFragment, { asPicture: pictureTagSources.length > 0, children: [pictureTagSources.length > 0
                ? pictureTagSources.map(pictureTagSource => pictureTagSource.origin !== 'none' && (_jsx("source", { media: pictureTagSource.media, srcSet: pictureTagSource.src }, pictureTagSource.media)))
                : undefined, _jsx(Styles.ResponsiveImage, { ...props, loading: computedLoading, sizes: pictureTagSources.length === 0 ? imgTagSizes : undefined, srcSet: pictureTagSources.length === 0 ? imgTagSrcset : undefined, alt: alt, "$aspectRatioXs": aspectRatios?.xs ?? null, "$aspectRatioSm": aspectRatios?.sm ?? null, "$aspectRatioMd": aspectRatios?.md ?? null, "$aspectRatioLg": aspectRatios?.lg ?? null, "$aspectRatioXl": aspectRatios?.xl ?? null, "$transparentBackground": transparentBackground, src: computedSrc, ref: ref })] }, computedSrc));
});
export const ResponsiveImageWithSkeletonWrapper = forwardRef((props, ref) => {
    const [loadedSrc, setLoadedSrc] = useState(null);
    const imageRef = useRef(null);
    if (loadedSrc && loadedSrc !== props.src) {
        setLoadedSrc(null);
    }
    return (_jsxs(Styles.ResponsiveImageWrapper, { children: [!loadedSrc && !props.transparentBackground ? (_jsx(Styles.ResponsiveImageSkeleton, { "$hasLoaded": !!loadedSrc })) : null, _jsx(ResponsiveImage, { ...props, onLoad: event => {
                    setLoadedSrc(props.src);
                    props.onLoad?.(event);
                }, ref: element => {
                    imageRef.current = element;
                    if (typeof ref === 'function') {
                        ref(element);
                    }
                    else if (ref) {
                        ref.current = element;
                    }
                    if (element?.complete) {
                        setLoadedSrc(props.src);
                    }
                } })] }));
});
export const ResponsiveImageWrapper = Styles.ResponsiveImageWrapper;
//# sourceMappingURL=ResponsiveImage.js.map